import React, { useRef, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import SignatureCanvas from 'react-signature-canvas';
import { SignatureAssetService } from 'src/service/SignatureAssetService';
import { SignatureDisplayConfig, SignatureDisplayConfigData } from './SignatureDisplayConfig';

interface SignatureDialogProps {
    visible: boolean;
    onHide: () => void;
    onSave: () => void;
    editSig?: any;
}

export const SignatureDialog = ({ visible, onHide, onSave, editSig }: SignatureDialogProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [signatureName, setSignatureName] = useState('');
    const [saving, setSaving] = useState(false);
    
    // Logic for Image Upload
    const [imageBase64, setImageBase64] = useState<string>('');
    const fileUploadRef = useRef<FileUpload>(null);
    
    // Logic for Signature Pad
    const padRef = useRef<SignatureCanvas>(null);
    const [penColor, setPenColor] = useState('black');

    // Logic for Display Config
    const [displayConfig, setDisplayConfig] = useState<SignatureDisplayConfigData>({
        showLabel: 'Y',
        labelText: 'Ký bởi:',
        showOwner: 'Y',
        ownerNameText: '',
        showAddress: 'N',
        emailText: '',
        showCertDetails: 'N',
        showPosition: 'N',
        positionText: '',
        showCompany: 'N',
        companyNameText: '',
        showSignTime: 'Y',
        showLogo: 'N',
        layoutStyle: 'STYLE_1'
    });
    
    const toast = useRef<Toast>(null);

    const getImageUrl = (sig: any) => {
        if (!sig) return '';
        if (sig.contentLocation && sig.contentLocation.startsWith('data:image')) {
            return sig.contentLocation;
        }
        return `/api/rest/s1/iam-signature-assets/resource?signatureAssetId=${sig.signatureAssetId}`;
    };

    useEffect(() => {
        const initData = async () => {
            const initialConfig: SignatureDisplayConfigData = {
                showLabel: 'Y',
                labelText: 'Ký bởi:',
                showOwner: 'Y',
                ownerNameText: '',
                showAddress: 'N',
                emailText: '',
                showCertDetails: 'N',
                showPosition: 'N',
                positionText: '',
                showCompany: 'N',
                companyNameText: '',
                showSignTime: 'Y',
                showLogo: 'N',
                layoutStyle: 'STYLE_1'
            };

            if (editSig) {
                setSignatureName(editSig.signatureName || '');
                setImageBase64(getImageUrl(editSig));

                // Lấy cấu hình riêng cho chữ ký đang edit
                try {
                    const res = await SignatureAssetService.getSignatureDisplayConfig(editSig.signatureAssetId);
                    if (res && res.displayConfig) {
                        setDisplayConfig({ ...initialConfig, ...res.displayConfig });
                    }
                } catch (err) {
                    console.error('Error fetching display config:', err);
                    setDisplayConfig(initialConfig);
                }
            } else {
                setSignatureName('');
                setImageBase64('');
                padRef.current?.clear();
                setActiveIndex(0);
                setDisplayConfig(initialConfig);
            }
        }

        if (visible) {
            initData();
        }
    }, [visible, editSig]);

    const validateDimensions = (file: File): Promise<{ width: number, height: number }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const onImageSelect = async (e: any) => {
        let file = e.files[0];
        if (file) {
            const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            if (!acceptedTypes.includes(file.type)) {
                toast.current?.show({ severity: 'error', summary: 'Sai định dạng', detail: 'Đại ca ơi, vui lòng chọn file .PNG hoặc .JPG/.JPEG nhé!', life: 3000 });
                fileUploadRef.current?.clear();
                return;
            }

            try {
                const { width, height } = await validateDimensions(file);
                // Giới hạn: 100-400px x 50-200px
                if (width < 100 || width > 400 || height < 50 || height > 200) {
                    toast.current?.show({ 
                        severity: 'error', 
                        summary: 'Kích thước không hợp lệ', 
                        detail: `Ảnh ${width}x${height}px không chuẩn. Yêu cầu: Rộng 100-400px, Cao 50-200px.`, 
                        life: 5000 
                    });
                    fileUploadRef.current?.clear();
                    setImageBase64('');
                    return;
                }

                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => setImageBase64(reader.result as string);
            } catch (error) {
                console.error('Error checking dimensions:', error);
            }
        }
    };

    const syncPadToImage = () => {
        if (activeIndex === 0 && padRef.current && !padRef.current.isEmpty()) {
            const dataUrl = padRef.current.getCanvas().toDataURL('image/png');
            setImageBase64(dataUrl);
            return dataUrl;
        }
        return imageBase64;
    };

    const handleSave = async () => {
        if (!signatureName.trim()) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Đại ca quên nhập tên gợi nhớ rồi!', life: 3000 });
            return;
        }

        try {
            setSaving(true);
            
            // Thu thập dữ liệu thông minh
            let finalBase64 = imageBase64;
            if (activeIndex === 0 && padRef.current && !padRef.current.isEmpty()) {
                finalBase64 = padRef.current.getCanvas().toDataURL('image/png');
            }

            if (!finalBase64) {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Đại ca vui lòng vẽ chữ ký hoặc chọn ảnh nhé!', life: 3000 });
                setSaving(false);
                return;
            }

            const typeId = 'AgtImageVerified';
            let newAssetId = editSig?.signatureAssetId;

            if (editSig) {
                // Nếu finalBase64 bắt đầu bằng /api/ hoặc đúng bằng contentLocation hiện tại thì nghĩa là KHÔNG đổi ảnh
                const isImageChanged = finalBase64.startsWith('data:image');
                
                if (!isImageChanged) {
                    // Chỉ cập nhật tên nếu có thay đổi
                    if (signatureName.trim() !== (editSig.signatureName || '')) {
                        await SignatureAssetService.updateSignatureAsset(editSig.signatureAssetId, {
                            signatureName: signatureName.trim()
                        });
                    }
                } else {
                    // Cập nhật cả ảnh và tên
                    await SignatureAssetService.updateSignatureAsset(editSig.signatureAssetId, {
                        signatureName: signatureName.trim(),
                        base64Data: finalBase64,
                        signatureTypeEnumId: typeId
                    });
                }
            } else {
                // Tạo mới
                const res = await SignatureAssetService.createSignatureAsset({
                    signatureName: signatureName.trim(),
                    base64Data: finalBase64,
                    signatureTypeEnumId: typeId
                });
                newAssetId = res.signatureAssetId;
            }

            // BƯỚC 2: Lưu cấu hình hiển thị đi kèm
            if (newAssetId) {
                const configToSave = { ...displayConfig, signatureAssetId: newAssetId };
                if (configToSave.displayConfigId) {
                    await SignatureAssetService.updateSignatureDisplayConfig(configToSave.displayConfigId, configToSave);
                } else {
                    await SignatureAssetService.createSignatureDisplayConfig(configToSave);
                }
            }

            toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã lưu chữ ký thành công!', life: 3000 });
            setTimeout(() => {
                onSave();
                onHide();
            }, 500);
        } catch (error: any) {
            console.error('Error saving:', error);
            const errorDetail = error.response?.data?.errors || 'Không thể lưu. Đại ca kiểm tra lại kết nối nhé!';
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: errorDetail, life: 5000 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog 
                header={editSig ? "Chỉnh sửa chữ ký" : "Thêm chữ ký mới"} 
                visible={visible} 
                onHide={onHide} 
                style={{ width: '1000px' }}
                maximizable
            >
                <div className="flex flex-column gap-4 py-2">
                    <div className="flex flex-column gap-2 px-2">
                        <label htmlFor="sigName" className="font-medium text-sm">Tên gợi nhớ (VD: Ký nháy, Con dấu...)</label>
                        <InputText 
                            id="sigName" 
                            value={signatureName} 
                            onChange={(e) => setSignatureName(e.target.value)} 
                            placeholder="Nhập tên gợi nhớ..."
                            autoFocus
                        />
                    </div>

                    <TabView 
                        activeIndex={activeIndex} 
                        onTabChange={(e) => {
                            if (activeIndex === 0) syncPadToImage();
                            setActiveIndex(e.index);
                        }}
                    >
                        <TabPanel header="Vẽ tay" leftIcon="pi pi-pencil mr-2">
                            <div className="flex flex-column align-items-center gap-3">
                                <div className="flex justify-content-between w-full align-items-center mb-1">
                                    <span className="text-xs text-color-secondary italic">Ký vào vùng 400x200px bên dưới</span>
                                    <div className="flex gap-2">
                                        <span className={`w-1rem h-1rem border-circle cursor-pointer border-1 ${penColor === 'black' ? 'border-primary shadow-2' : 'surface-border'}`} style={{ backgroundColor: 'black' }} onClick={() => setPenColor('black')} />
                                        <span className={`w-1rem h-1rem border-circle cursor-pointer border-1 ${penColor === '#0052cc' ? 'border-primary shadow-2' : 'surface-border'}`} style={{ backgroundColor: '#0052cc' }} onClick={() => setPenColor('#0052cc')} />
                                    </div>
                                </div>
                                <div className="border-1 surface-border border-round-md bg-white shadow-1" style={{ width: '400px', height: '200px', position: 'relative' }}>
                                    <SignatureCanvas 
                                        ref={padRef}
                                        penColor={penColor}
                                        canvasProps={{ width: 400, height: 200, className: 'signature-canvas' }}
                                        backgroundColor="rgba(255, 255, 255, 0)"
                                    />
                                </div>
                                <Button label="Xóa trơn" icon="pi pi-refresh" text size="small" onClick={() => padRef.current?.clear()} />
                            </div>
                        </TabPanel>
                        <TabPanel header="Tải ảnh" leftIcon="pi pi-upload mr-2">
                            <div className="flex flex-column gap-3">
                                <div className="flex justify-content-between align-items-center">
                                    <span className="text-xs text-color-secondary italic">Dễ dàng tải ảnh chữ ký có sẵn của đại ca lên hệ thống.</span>
                                </div>
                                <FileUpload 
                                    ref={fileUploadRef}
                                    mode="basic" 
                                    name="demo[]" 
                                    accept="image/png,image/jpeg" 
                                    maxFileSize={1000000} 
                                    onSelect={onImageSelect}
                                    auto
                                    chooseLabel="Chọn ảnh chữ ký"
                                    className="p-button-outlined"
                                />
                                <span className="text-color-secondary text-xs">Chấp nhận file .PNG, .JPG, .JPEG. Dung lượng tối đa 1MB.</span>
                                {imageBase64 && (
                                    <div className="flex justify-content-center p-3 border-1 surface-border border-round-md bg-surface-50">
                                        <img src={imageBase64} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }} />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        <TabPanel header="Hiển thị" leftIcon="pi pi-sliders-h mr-2">
                            <SignatureDisplayConfig 
                                previewImageBase64={imageBase64} 
                                config={displayConfig}
                                onChange={setDisplayConfig}
                            />
                        </TabPanel>
                    </TabView>

                    <div className="flex justify-content-end gap-2 mt-2 px-2">
                        <Button label="Hủy" icon="pi pi-times" outlined severity="secondary" onClick={onHide} disabled={saving} />
                        <Button label="Lưu chữ ký" icon="pi pi-save" onClick={handleSave} loading={saving} />
                    </div>
                </div>
            </Dialog>
        </>
    );
};
